import * as React from "react";
import { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";
import { CheckCircle, AlertTriangle, XCircle, RefreshCw } from "@/components/ds/icons";

// ── Types ────────────────────────────────────────────────────────────────────

type HealthStatus = "Healthy" | "Degraded" | "Failed" | "NotConfigured";

/**
 * Display-only status: Failed/Degraded rows that have NEVER reported Healthy
 * (firstHealthyAt is null) render as this neutral state instead of an alarming
 * red/amber pill. A brand-new integration with a bad token on its first-ever
 * probe is a setup problem, not a regression — see feedback_no_false_positive_alerts.
 */
type DisplayStatus = HealthStatus | "SettingUp";

interface IntegrationHealthRow {
  slug: string;
  siteId: number;
  status: HealthStatus;
  latencyMs: number | null;
  marker: string | null;
  detail: string | null;
  checkedAt: string | null;
  consecutiveFailures: number;
  circuitOpenUntil: string | null;
  /** Null means this integration has never once reported Healthy for this site. */
  firstHealthyAt: string | null;
}

interface Props {
  slug: string;
  className?: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

/** Scheduler runs every 15 min — a result older than this (or missing) is stale. */
const STALE_AFTER_MS = 30 * 60_000;

/** Client-side spam-click guard, separate from + in addition to the backend circuit breaker. */
const CLIENT_COOLDOWN_MS = 30_000;

/** Cooldown applied when the backend reports its circuit is open, if we can't parse retryAfter. */
const CIRCUIT_OPEN_FALLBACK_COOLDOWN_MS = 5 * 60_000;

// ── Helpers ──────────────────────────────────────────────────────────────────

function ago(iso: string | null): string {
  if (!iso) return "never checked";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "never checked";
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function isStale(checkedAt: string | null, status: HealthStatus): boolean {
  if (status === "NotConfigured") return false;
  if (!checkedAt) return true;
  const d = new Date(checkedAt);
  if (isNaN(d.getTime())) return true;
  return Date.now() - d.getTime() > STALE_AFTER_MS;
}

function StatusIcon({ status }: { status: DisplayStatus }) {
  if (status === "Healthy")
    return <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" aria-hidden="true" />;
  if (status === "Degraded")
    return <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" aria-hidden="true" />;
  if (status === "Failed")
    return <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" aria-hidden="true" />;
  // NotConfigured and SettingUp share the same neutral dot — neither is an incident.
  return <span className="w-2 h-2 rounded-full bg-zinc-300 shrink-0 inline-block" aria-hidden="true" />;
}

function pillClasses(status: DisplayStatus): string {
  if (status === "Healthy") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (status === "Degraded") return "bg-amber-100 text-amber-700 border-amber-200";
  if (status === "Failed") return "bg-red-100 text-red-700 border-red-200";
  return "bg-zinc-100 text-zinc-500 border-zinc-200";
}

function displayLabel(status: DisplayStatus): string {
  return status === "SettingUp" ? "Not yet connected" : status;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Live per-integration health pill. Reads from the backend Integration Health
 * Check framework (`GET/POST Integration/Health/{slug}`) — site-scoped and
 * IDOR-safe server-side. Every integration's own Accounts/config page should
 * mount one of these next to its static connect/enabled toggle, since that
 * toggle only reflects a DB flag set at connect time and is never re-validated.
 */
export default function IntegrationHealthBadge({ slug, className }: Props) {
  const api = useApi();
  const [row, setRow] = useState<IntegrationHealthRow | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [running, setRunning] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [circuitMessage, setCircuitMessage] = useState<string | null>(null);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, forceTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.messaging.get(`Integration/Health/${encodeURIComponent(slug)}`);
      if (resp.status === 404) {
        setNotFound(true);
        setRow(null);
        return;
      }
      if (!resp.ok) {
        const bodyText = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status}${bodyText ? `: ${bodyText}` : ""}`);
      }
      setNotFound(false);
      setRow(await resp.json());
    } catch (err) {
      console.error(`IntegrationHealthBadge[${slug}] failed to load health:`, err);
      setError(`Failed to load health: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [api.messaging, slug]);

  useEffect(() => { load(); }, [load]);

  // Re-render every 30s so "Xm ago" text stays fresh.
  useEffect(() => {
    const t = setInterval(() => forceTick(n => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  // While a cooldown is active, tick every second so the "Check now (Xs)"
  // countdown actually counts down instead of jumping from one stale value
  // straight to enabled.
  useEffect(() => {
    if (cooldownUntil == null || cooldownUntil <= Date.now()) return;
    cooldownTimerRef.current = setInterval(() => {
      forceTick(n => n + 1);
      if (cooldownUntil <= Date.now() && cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    }, 1_000);
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    };
  }, [cooldownUntil]);

  const runNow = useCallback(async () => {
    if (running) return;
    setRunning(true);
    setCircuitMessage(null);
    try {
      const resp = await api.messaging.post(`Integration/Health/${encodeURIComponent(slug)}/run`, {});
      if (resp.status === 429) {
        let retryAfter: string | null = null;
        try {
          const body = await resp.json();
          retryAfter = body?.retryAfter ?? null;
        } catch (parseErr) {
          setError(`Failed to parse circuit-open response: ${parseErr instanceof Error ? parseErr.message : String(parseErr)}`);
        }
        const retryDate = retryAfter ? new Date(retryAfter) : null;
        const until = retryDate && !isNaN(retryDate.getTime())
          ? retryDate.getTime()
          : Date.now() + CIRCUIT_OPEN_FALLBACK_COOLDOWN_MS;
        setCooldownUntil(until);
        setCircuitMessage("Too many recent failures — will retry automatically" + (retryDate ? ` at ${retryDate.toLocaleTimeString()}` : ""));
        return;
      }
      if (!resp.ok) {
        const bodyText = await resp.text().catch(() => "");
        console.error(`IntegrationHealthBadge[${slug}] check-now failed: HTTP ${resp.status}`, bodyText);
        setError(`Check now failed (HTTP ${resp.status})${bodyText ? `: ${bodyText}` : ""}`);
        return;
      }
      setNotFound(false);
      setRow(await resp.json());
    } catch (err) {
      console.error(`IntegrationHealthBadge[${slug}] check-now threw before a response was received:`, err);
      setError(
        err instanceof TypeError
          ? "Could not reach the server (network error or CORS block)."
          : `Check now failed: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      // Deliberate UX-level rate limit — 30s client-side cooldown after any
      // response (success or failure), separate from the backend's own
      // circuit breaker, to avoid hammering the third party on repeat clicks.
      setCooldownUntil(prev => prev && prev > Date.now() ? prev : Date.now() + CLIENT_COOLDOWN_MS);
      setRunning(false);
    }
  }, [api.messaging, slug, running]);

  const cooldownActive = cooldownUntil != null && cooldownUntil > Date.now();
  const cooldownSecondsLeft = cooldownActive ? Math.ceil((cooldownUntil! - Date.now()) / 1000) : 0;

  if (loading && !row && !notFound && !error) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-zinc-50 text-zinc-400 border-zinc-200 text-xs ${className ?? ""}`}>
        Checking…
      </span>
    );
  }

  if (error && !row && !notFound) {
    return (
      <span role="alert" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-red-50 text-red-600 border-red-200 text-xs ${className ?? ""}`}>
        {error}
      </span>
    );
  }

  const status: HealthStatus = notFound || !row ? "NotConfigured" : row.status;
  const checkedAt = row?.checkedAt ?? null;
  const stale = isStale(checkedAt, status);

  // Failed/Degraded with no FirstHealthyAt = never successfully connected once —
  // a setup problem (bad/missing credentials), not a regression. Render neutral,
  // not alarming. See feedback_no_false_positive_alerts.
  const neverConnected = (status === "Failed" || status === "Degraded") && !row?.firstHealthyAt;
  const displayStatus: DisplayStatus = neverConnected ? "SettingUp" : status;

  return (
    <div className={`inline-flex items-center gap-2 flex-wrap ${className ?? ""}`}>
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium ${pillClasses(displayStatus)} ${stale ? "opacity-60" : ""}`}
        title={
          neverConnected
            ? "This integration hasn't connected successfully yet — check its credentials."
            : stale
              ? "Health data may be outdated — the 15-minute probe scheduler hasn't reported in recently"
              : undefined
        }
      >
        <StatusIcon status={displayStatus} />
        {notFound ? "Not yet checked" : displayLabel(displayStatus)}
      </span>
      {!notFound && (
        <span className="text-xs text-zinc-400">
          {ago(checkedAt)}
          {stale && " · may be outdated"}
        </span>
      )}
      <button
        type="button"
        onClick={runNow}
        disabled={running || cooldownActive}
        aria-label={`Check ${slug} connection now`}
        className="inline-flex items-center gap-1 text-xs text-[color:var(--color-primary)] hover:underline disabled:text-zinc-400 disabled:no-underline disabled:cursor-not-allowed"
      >
        <RefreshCw className={`w-3 h-3 ${running ? "animate-spin" : ""}`} aria-hidden="true" />
        {running ? "Checking…" : cooldownActive ? `Check now (${cooldownSecondsLeft}s)` : "Check now"}
      </button>
      {circuitMessage && (
        <span role="alert" className="text-xs text-amber-600">{circuitMessage}</span>
      )}
      {error && row && (
        <span role="alert" className="text-xs text-red-500">{error}</span>
      )}
    </div>
  );
}
